import { isString } from 'lodash-es'
import { matchPath } from 'react-router'

import { Routes } from '@/lib/routes'
import { CancellationFlightOrHotelDetails } from '@/travelsuit'
import { BookingSegmentId } from '@/types/trip'

import history from './history'
import { dumpQueryString, parseQueryString } from './utils'

export { matchPath }

type TripRouteState = {
	originalBookingSegmentId?: BookingSegmentId
	next?: boolean
	fromCombinedSearch?: boolean
}

export type RouteState = {
	[Routes.BookCustomerSuccessConsultant]: {
		backPath?: string
	}
	[Routes.BrowseTrip]: TripRouteState
	[Routes.BuildTrip]: TripRouteState
	[Routes.TripProductSearch]: TripRouteState
	[Routes.TripProductSearchWithStep]: TripRouteState
	[Routes.CreateInvoiceProfile]: {
		invoiceProfileId?: number
		isAfterCreditCardVerification?: boolean
	}
	[Routes.HotelResult]: {
		originalBookingSegmentId?: number
	}
	[Routes.Itinerary]: {
		isBookingSidebarOpen?: boolean
		nameOfSuccessfullyCancelledService?: string
		cancellationFlightDetails?: CancellationFlightOrHotelDetails
	}
	[Routes.TripBuilder]: TripRouteState
	[Routes.TripProduct]: {
		railSearchId?: number
	}
}

interface RouteForOptions {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	hash?: string | Record<string, any>
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	queryString?: string | Record<string, any>
}

export function routeFor(
	route: string,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	params: Record<string, any> | undefined = undefined,
	{ hash, queryString }: RouteForOptions = {},
) {
	let routeStr = route
		.replace(/:([_a-z0-9]+)\??/gi, (_str, match) => String(params?.[match] ?? ''))
		.replace(/\/+/, '/')
		.replace(/\/$/, '')
	if (!routeStr.length) {
		routeStr = '/'
	}
	if (queryString) {
		routeStr += '?' + (typeof queryString === 'string' ? queryString : dumpQueryString(queryString))
	}
	if (hash) {
		routeStr += '#' + (typeof hash === 'string' ? hash : dumpQueryString(hash))
	}
	routeStr = routeStr.replace(/\/+/g, '/')
	return routeStr
}

interface RouteOptions {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	queryString?: Record<string, any> | string
	useReturnTo?: boolean
	forceReloadSameRoute?: boolean
	replace?: boolean
}

export type RouteWithParams<R extends string> = R extends `/:${string}`
	? R
	: R extends `/${string}/:${string}`
		? R
		: never
export type RouteWithoutParams<R extends string> = RouteWithParams<R> extends never ? R : never

type GetParamRecord<P extends string> = P extends `${infer Name}?`
	? { [K in Name]?: string | number }
	: { [K in P]: string | number }
type MergeIfDefined<
	P extends Record<string, string | number> | undefined,
	X extends Record<string, string | number> | undefined,
> = P extends undefined ? X : X extends undefined ? P : P & X

export type RouteParams<
	R extends string,
	P extends Record<string, string | number> | undefined = undefined,
> = R extends `/:${infer Name}/${infer Suffix}`
	? // @ts-expect-error todo if you see this please remove this comment and fix the type error
		MergeIfDefined<MergeIfDefined<P, GetParamRecord<Name>>, RouteParams<`/${Suffix}`>>
	: R extends `/:${infer Name}`
		? // @ts-expect-error todo if you see this please remove this comment and fix the type error
			MergeIfDefined<P, GetParamRecord<Name>>
		: R extends `/${string}/:${infer Suffix}`
			? MergeIfDefined<P, RouteParams<`/:${Suffix}`>>
			: MergeIfDefined<P, undefined>

/** @deprecated use useNavigate instead */
export function redirectTo<R extends Routes>(
	route: RouteWithoutParams<R>,
	params?: undefined,
	opts?: RouteOptions,
): void
export function redirectTo<R extends Routes, P extends RouteParams<R>>(
	route: RouteWithParams<R>,
	params: P,
	opts?: RouteOptions,
): void
export function redirectTo<R extends keyof RouteState>(
	route: RouteWithoutParams<R>,
	params: undefined,
	opts: RouteOptions | undefined,
	state: RouteState[R],
): void
export function redirectTo<R extends keyof RouteState, P extends RouteParams<R>>(
	route: RouteWithParams<R>,
	params: P,
	opts: RouteOptions | undefined,
	state: RouteState[R],
): void
export function redirectTo<R extends Routes, P extends RouteParams<R>>(
	route: R,
	params: P | undefined = undefined,
	routeOptions: RouteOptions = {},
	state?: R extends keyof RouteState ? RouteState[R] : never,
): void {
	const { queryString = {}, useReturnTo = false, replace = false } = routeOptions

	const _route = useReturnTo
		? routeWithReturnTo(route, params, { queryString })
		: routeFor(route, params, { queryString })

	// FIX: Let clean `location.search` if `queryString` provided
	if (document.location.pathname === _route.replace(/\?$/, '') && !state && !('queryString' in routeOptions)) {
		return
	}
	if (replace) {
		history.replace(_route, state)
	} else {
		history.push(_route, state)
	}
}

export function redirectToLogin({ useReturnTo = true, queryString }: RouteOptions = {}): void {
	return redirectTo(Routes.Login, undefined, { useReturnTo, queryString })
}

export function routeWithReturnTo(
	route: Routes,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	params: Record<string, any> | undefined = undefined,
	{ queryString: _queryString = {} }: Omit<RouteOptions, 'useReturnTo'> = {},
): string {
	const currentPath = getCurrentPath()
	return routeFor(route, params, {
		queryString: isString(_queryString) ? _queryString : { returnTo: currentPath, ..._queryString },
	})
}

export function loginRoute({ useReturnTo = true, queryString: _queryString = {} }: RouteOptions = {}): string {
	if (!useReturnTo) {
		return routeFor(Routes.Login, undefined, { queryString: _queryString })
	}
	return routeWithReturnTo(Routes.Login, undefined, { queryString: _queryString })
}

function getCurrentPath({ removeReturnTo = true }: { removeReturnTo?: boolean } = {}): string {
	const curQs = parseQueryString(document.location.search)
	if (removeReturnTo) {
		delete curQs.returnTo
	}
	return [document.location.pathname, dumpQueryString(curQs)].filter(Boolean).join('?')
}
